import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import ActionNotice from "@/components/base/ActionNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { videos } from "@/mocks/videos";
import { copyCurrentLink } from "@/utils/share";
import { formatDate, formatDuration, formatViews } from "@/utils/format";

const demoComments = [
  {
    author: "灯下",
    time: "2 天前",
    content: "光影处理得很舒服，尤其是走廊那段，几乎没有台词但情绪很足。",
  },
  {
    author: "弦外",
    time: "4 天前",
    content: "配乐加分，低频的处理很克制，戴耳机看完全是另一种体验。",
  },
  {
    author: "白噪音",
    time: "1 周前",
    content: "节奏偏慢，但我反而喜欢这种不着急讲故事的方式。",
  },
];

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((item) => item.id === id);
  const [notice, setNotice] = useState("");
  const { isFavorite, toggle } = useFavorites("video");

  usePageMeta({
    title: video ? `${video.title} - ${video.category}视频 - 成人网` : "视频详情 - 成人网",
    description: video
      ? `${video.title}（${video.category} · ${video.durationMinutes} 分钟 · ${video.resolution}）：${video.description.slice(0, 60)}… 成人网演示视频详情页。`
      : "成人网视频详情页，包含演示播放区域、作品简介与相关推荐。",
    keywords: video
      ? `${video.title},${video.category}视频,成人视频,${video.tags.join(",")}`
      : "成人视频,视频详情,成人网",
  });

  const recommended = useMemo(() => {
    if (!video) {
      return videos.slice(0, 3);
    }
    const sameCategory = videos.filter(
      (item) => item.id !== video.id && item.category === video.category,
    );
    const others = videos.filter(
      (item) => item.id !== video.id && item.category !== video.category,
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [video]);

  if (!video) {
    return (
      <div className="container-page py-10">
        <EmptyState
          icon="ri-film-line"
          title="没有找到这个视频"
          description="链接可能已经失效，或者这条演示内容已被移动。可以先回到视频分类继续浏览。"
          actionLabel="返回视频分类"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/video-category");
          }}
        />
      </div>
    );
  }

  const favorited = isFavorite(video.id);

  const handleFavorite = () => {
    const added = toggle(video.id);
    setNotice(added ? "已加入收藏（仅保存在当前设备）" : "已取消收藏");
  };

  const handleShare = async () => {
    const ok = await copyCurrentLink();
    setNotice(ok ? "链接已复制，可以分享给朋友了" : "复制失败，请手动复制浏览器地址");
  };

  return (
    <div className="container-page py-7">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "视频分类", to: "/video-category" },
          { label: video.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-background-900">
            <img
              src={video.cover}
              alt={`${video.title} 视频封面`}
              title={`${video.title} ${video.category} 视频`}
              className="h-full w-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/30 to-background-950/20"></div>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <button
                type="button"
                onClick={() => setNotice("演示模式：当前页面不包含真实播放与流媒体接入")}
                className="group flex h-16 w-16 items-center justify-center rounded-full border border-foreground-700/40 bg-background-950/40 text-2xl text-foreground-950 transition-colors hover:border-accent-500 hover:text-accent-500 cursor-pointer"
                aria-label="播放（演示）"
              >
                <i className="ri-play-fill ml-0.5"></i>
              </button>
              <p className="mt-4 text-[11px] tracking-[0.2em] text-foreground-600">
                演示模式 · 不含真实播放
              </p>
            </div>

            <div className="absolute inset-x-0 bottom-0 px-4 pb-3">
              <div className="h-1 w-full overflow-hidden rounded-full bg-foreground-800/20">
                <div className="h-full w-[18%] rounded-full bg-accent-500"></div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-foreground-600">
                <span className="flex items-center gap-2">
                  <i className="ri-play-fill"></i>
                  00:00
                  <span className="text-foreground-500">/</span>
                  {formatDuration(video.durationMinutes)}
                </span>
                <span className="flex items-center gap-3">
                  <i className="ri-volume-up-line"></i>
                  <i className="ri-fullscreen-line"></i>
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded-sm bg-primary-500/20 px-2 py-0.5 text-accent-500">
                {video.category}
              </span>
              <span className="rounded-sm bg-background-200 px-2 py-0.5 text-foreground-500">
                {video.resolution}
              </span>
              <span className="text-foreground-500">{formatDate(video.uploadDate)} 上传</span>
            </div>
            <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-[30px]">
              {video.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {formatDuration(video.durationMinutes)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)} 次播放
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-hd-line"></i>
                {video.resolution}
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                  favorited
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-500 hover:text-accent-500"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
              <Link
                to="/community"
                className="flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap"
              >
                <i className="ri-chat-3-line"></i>
                去社区讨论
              </Link>
            </div>
          </div>

          <section className="mt-7 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="text-sm font-medium text-foreground-950">作品简介</h2>
            <p className="mt-2.5 text-[13px] leading-[1.9] text-foreground-600">
              {video.description}
            </p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-200 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground-950">评论（演示）</h2>
              <span className="text-[11px] text-foreground-500">共 3 条演示评论</span>
            </div>
            <div className="mt-4 space-y-4">
              {demoComments.map((comment) => (
                <div key={comment.author} className="flex gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-200 text-xs text-accent-500">
                    {comment.author.slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1 border-b border-background-200/70 pb-3">
                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      <span className="text-foreground-800">{comment.author}</span>
                      <span className="text-foreground-500">{comment.time}</span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/community"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-background-300 py-2.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              <i className="ri-chat-3-line"></i>
              评论功能演示中，前往社区参与讨论
            </Link>
          </section>
        </div>

        <aside className="min-w-0 space-y-6">
          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">内容信息</h2>
            <dl className="mt-3 space-y-2.5 text-xs">
              {[
                { label: "分类", value: video.category },
                { label: "时长", value: `${video.durationMinutes} 分钟` },
                { label: "清晰度", value: video.resolution },
                { label: "上传时间", value: formatDate(video.uploadDate) },
                { label: "播放量", value: `${formatViews(video.views)} 次` },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 rounded-md bg-background-50 p-2.5 text-[10.5px] leading-relaxed text-foreground-500">
              本站视频均为演示条目，播放按钮仅作界面展示，不包含真实播放权限。
            </p>
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">你可能还喜欢</h2>
            <div className="mt-3 space-y-1">
              {recommended.map((item) => (
                <Link
                  key={item.id}
                  to={`/video-detail/${item.id}`}
                  className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-200/50"
                >
                  <div className="h-[46px] w-[76px] shrink-0 overflow-hidden rounded bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 推荐视频封面`}
                      title={`${item.title} 成人视频`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-xs text-foreground-900 group-hover:text-accent-500">
                      {item.title}
                    </p>
                    <p className="mt-1 text-[10px] text-foreground-500">
                      {item.category} · {formatDuration(item.durationMinutes)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <section className="mt-12">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-lg text-foreground-950">相关推荐</h2>
          <Link
            to="/video-category"
            className="text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
          >
            更多视频
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {recommended.map((item) => (
            <VideoCard key={item.id} video={item} />
          ))}
        </div>
      </section>

      {notice ? <ActionNotice message={notice} onClose={() => setNotice("")} /> : null}
    </div>
  );
}